/**
 * SoleadLab 2.0 - Project Export (ZIP)
 *
 * Creates a complete ground-truth ZIP backup of the current project.
 * Downloads all binary assets (overlays, orthophotos, photos) and
 * packages them with the project JSON data.
 */

import JSZip from 'jszip';
import type { ProjectData, ProjectMeta } from '../stores/projectStore';
import { useAuthStore } from '../stores/authStore';
import { getAllPhotoBlobs } from '../utils/photoBlobs';

export interface ExportManifest {
  version: string;
  exportedAt: string;
  exportedBy: string | null;
  siteName: string | null;
  siteId: string | null;
}

/**
 * Fetch a binary asset from a URL (R2, blob, or data URL)
 */
async function fetchAssetBlob(url: string): Promise<Blob | null> {
  try {
    if (url.startsWith('blob:') || url.startsWith('data:') || url.startsWith('http')) {
      const res = await fetch(url);
      if (!res.ok) return null;
      return await res.blob();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Try to determine file extension from URL or content type
 */
function guessExt(url: string, contentType?: string): string {
  // Check URL extension
  const urlMatch = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
  if (urlMatch) return urlMatch[1].toLowerCase();

  // Check content type
  if (contentType?.includes('png')) return 'png';
  if (contentType?.includes('jpeg') || contentType?.includes('jpg')) return 'jpg';
  if (contentType?.includes('gif')) return 'gif';
  if (contentType?.includes('webp')) return 'webp';
  if (contentType?.includes('tiff')) return 'tiff';

  return 'png'; // default
}

/**
 * Export the current project as a ZIP file
 */
export async function exportProjectZip(
  data: ProjectData,
  meta: ProjectMeta,
  onProgress?: (message: string) => void
): Promise<Blob> {
  const zip = new JSZip();

  // Clone data to modify URLs to relative paths
  const exportData = JSON.parse(JSON.stringify(data)) as ProjectData;

  const assetsFolder = zip.folder('assets')!;
  const overlaysFolder = assetsFolder.folder('overlays')!;
  const orthosFolder = assetsFolder.folder('orthophotos')!;
  const photosFolder = assetsFolder.folder('photos')!;
  const geoPhotosFolder = assetsFolder.folder('geo_photos')!;

  // ── Export overlays ──
  onProgress?.('Exporting overlays...');
  for (let i = 0; i < exportData.overlays.length; i++) {
    const overlay = exportData.overlays[i];
    if (!overlay.url) continue;

    const blob = await fetchAssetBlob(overlay.url);
    if (blob) {
      const ext = guessExt(overlay.url, blob.type);
      const filename = `${overlay.id}.${ext}`;
      overlaysFolder.file(filename, blob);
      exportData.overlays[i] = { ...overlay, url: `assets/overlays/${filename}` };
    }
  }

  // ── Export orthophotos ──
  onProgress?.('Exporting orthophotos...');
  for (let i = 0; i < exportData.orthophotos.length; i++) {
    const ortho = exportData.orthophotos[i];
    if (!ortho.url) continue;

    const blob = await fetchAssetBlob(ortho.url);
    if (blob) {
      const ext = guessExt(ortho.url, blob.type);
      const filename = `${ortho.id}.${ext}`;
      orthosFolder.file(filename, blob);
      exportData.orthophotos[i] = { ...ortho, url: `assets/orthophotos/${filename}` };
    }
  }

  // ── Export photo indications ──
  onProgress?.('Exporting photos...');
  const photoBlobs = getAllPhotoBlobs();
  for (let i = 0; i < exportData.indications.length; i++) {
    const ind = exportData.indications[i];
    if (ind.type !== 'Photo' || !ind.properties.image) continue;

    const imagePath = ind.properties.image;
    let blob: Blob | null = null;

    // Try photoBlobs map first (local files)
    const entry = photoBlobs.get(imagePath);
    if (entry?.file) {
      blob = entry.file;
    } else {
      // Try as URL (R2)
      blob = await fetchAssetBlob(imagePath);
    }

    if (blob) {
      const ext = guessExt(imagePath, blob.type);
      const filename = `${ind.id}.${ext}`;
      photosFolder.file(filename, blob);
      exportData.indications[i] = {
        ...ind,
        properties: { ...ind.properties, image: `assets/photos/${filename}` },
      };
    }
  }

  // ── Export geo photos ──
  onProgress?.('Exporting geo photos...');
  for (let i = 0; i < exportData.geoPhotos.length; i++) {
    const gp = exportData.geoPhotos[i];
    if (!gp.url) continue;

    const blob = await fetchAssetBlob(gp.url);
    if (blob) {
      const ext = guessExt(gp.url, blob.type);
      const filename = `${gp.id}.${ext}`;
      geoPhotosFolder.file(filename, blob);
      exportData.geoPhotos[i] = { ...gp, url: `assets/geo_photos/${filename}` };
    }
  }

  // ── Manifest ──
  const user = useAuthStore.getState().user;

  const manifest: ExportManifest = {
    version: '2.0',
    exportedAt: new Date().toISOString(),
    exportedBy: user?.email || null,
    siteName: meta.siteName || null,
    siteId: meta.siteId || null,
  };

  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  zip.file('project.json', JSON.stringify(exportData, null, 2));

  // ── Generate ZIP ──
  onProgress?.('Generating ZIP file...');
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
}

/**
 * Trigger download of a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
